import { jsPDF } from 'jspdf';
import { CLP, numeroAPalabras, labelMedio, fmtFechaHora } from '../utils/finance';
import { normalizeBranch as normalizeBranchPdf, loadLogos, renderStandardHeader, renderStandardFooter } from './pdfCommon';

// Paleta en RGB para jsPDF
const C = {
  primary: [26, 58, 92],
  teal: [44, 184, 160],
  text: [30, 30, 30],
  muted: [110, 120, 130],
  light: [244, 246, 248],
  border: [220, 225, 230],
};

const DEFAULT_BRANCH = {
  name: 'Hospital Clínico Veterinario UDLA',
  address: 'Av. República 71, Santiago',
  phone: '+56 2 2222 2222',
  email: '',
  website: '',
  rut: '71.234.567-8',
};

const normalizeBranch = (b) => ({
  ...normalizeBranchPdf(b),
  rut: b?.rut || DEFAULT_BRANCH.rut,
});

const toDate = (v) => {
  if (!v) return new Date();
  if (typeof v?.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? new Date() : d;
};

const TITULO_DOC = {
  boleta: 'BOLETA DE VENTA',
  factura: 'FACTURA',
  exento: 'BOLETA EXENTA',
};

/**
 * Genera el PDF de una boleta/factura de venta.
 * @param {object} venta   Documento de Sales (con items, totales, pagos)
 * @param {object} owner   Datos del propietario { name, rut, celular, email }
 * @param {object} patient Datos del paciente { name, species }
 * @param {object} opts    { clinic, autoPrint, format: 'a4' | 'ticket' }
 * @returns {jsPDF}
 */
export async function generateBoletaPDF(venta = {}, owner = {}, patient = {}, opts = {}) {
  const branch = normalizeBranch(opts.branch || opts.clinic);
  const autoPrint = opts.autoPrint !== false;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = 210;
  const margin = 16;
  const cw = pw - margin * 2;

  const logos = await loadLogos();
  const docTitle = TITULO_DOC[venta.tipoDocumento] || 'BOLETA DE VENTA';
  const docNumber = venta.folioDocumento || venta.boletaNumber || 'S/N';
  const docDate = fmtFechaHora(venta.createdAt || venta.paidAt || new Date());
  renderStandardHeader(doc, branch, logos, {
    title: docTitle,
    number: docNumber,
    date: docDate,
  });

  let y = 40;
  const setText = (rgb) => doc.setTextColor(...rgb);
  const setFill = (rgb) => doc.setFillColor(...rgb);
  const setDraw = (rgb) => doc.setDrawColor(...rgb);

  // ── Datos propietario / paciente ──────────────────────────
  setText(C.text);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Propietario', margin, y);
  doc.text('Paciente', margin + cw / 2, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setText(C.muted);
  const ownerName = venta.ownerName || owner.name || '–';
  const ownerRut = venta.ownerRut || owner.rut || '';
  doc.text(`${ownerName}${ownerRut ? `  ·  RUT: ${ownerRut}` : ''}`, margin, y + 5);
  const pName = venta.patientName || patient.name || '–';
  const pSpecies = patient.species || '';
  doc.text(`${pName}${pSpecies ? `  ·  ${pSpecies}` : ''}`, margin + cw / 2, y + 5);

  y += 12;

  // ── Tabla de ítems ────────────────────────────────────────
  const cols = [
    { key: 'descripcion', label: 'Descripción', x: margin, w: cw - 78, align: 'left' },
    { key: 'cantidad', label: 'Cant.', x: margin + cw - 78, w: 16, align: 'center' },
    { key: 'precioUnitario', label: 'P. Unit.', x: margin + cw - 62, w: 30, align: 'right' },
    { key: 'total', label: 'Total', x: margin + cw - 32, w: 32, align: 'right' },
  ];

  setFill(C.light);
  doc.rect(margin, y, cw, 7, 'F');
  setText(C.primary);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  cols.forEach((c) => {
    const tx = c.align === 'right' ? c.x + c.w : c.align === 'center' ? c.x + c.w / 2 : c.x + 1.5;
    doc.text(c.label, tx, y + 4.7, { align: c.align });
  });
  y += 7;

  doc.setFont('helvetica', 'normal');
  setText(C.text);
  const items = Array.isArray(venta.items) ? venta.items : [];
  items.forEach((it, idx) => {
    const descLines = doc.splitTextToSize(String(it.descripcion || '–'), cols[0].w - 3);
    const rowH = Math.max(6, descLines.length * 4 + 2);
    if (y + rowH > 262) { doc.addPage(); y = margin; }
    if (idx % 2 === 1) { setFill([250, 251, 252]); doc.rect(margin, y, cw, rowH, 'F'); }
    setText(C.text);
    doc.setFontSize(8);
    doc.text(descLines, cols[0].x + 1.5, y + 4);
    doc.text(String(it.cantidad ?? 1), cols[1].x + cols[1].w / 2, y + 4, { align: 'center' });
    doc.text(CLP(it.precioUnitario), cols[2].x + cols[2].w, y + 4, { align: 'right' });
    doc.text(CLP(it.total), cols[3].x + cols[3].w, y + 4, { align: 'right' });
    y += rowH;
  });

  setDraw(C.border);
  doc.setLineWidth(0.3);
  doc.line(margin, y, margin + cw, y);
  y += 5;

  // ── Totales ───────────────────────────────────────────────
  const totX = margin + cw - 62;
  const valX = margin + cw;
  const totalRow = (label, val, bold = false, color = C.text) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 10 : 8.5);
    setText(color);
    doc.text(label, totX, y, { align: 'right' });
    doc.text(CLP(val), valX, y, { align: 'right' });
    y += bold ? 7 : 5;
  };

  if (venta.descuentoTotal > 0) totalRow('Descuentos:', -venta.descuentoTotal, false, C.danger || C.muted);
  if (venta.montoExento > 0) totalRow('Exento:', venta.montoExento);
  totalRow('Neto:', venta.subtotalNeto);
  totalRow('IVA (19%):', venta.montoIVA);
  setDraw(C.border);
  doc.line(totX - 20, y - 2, valX, y - 2);
  y += 2;
  totalRow('TOTAL:', venta.totalFinal, true, C.primary);

  y += 3;
  setText(C.muted);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  const enPalabras = `Son: ${numeroAPalabras(venta.totalFinal)}`;
  doc.splitTextToSize(enPalabras, cw).forEach((ln) => { doc.text(ln, margin, y); y += 4; });

  y += 4;

  // ── Medios de pago ────────────────────────────────────────
  setText(C.text);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Forma de pago', margin, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setText(C.muted);
  const pagos = Array.isArray(venta.pagos) ? venta.pagos : [];
  if (pagos.length) {
    pagos.forEach((p) => {
      const ref = p.referencia ? `  ·  Ref: ${p.referencia}` : '';
      const cuotas = p.cuotas ? `  ·  ${p.cuotas} cuotas` : '';
      doc.text(`${labelMedio(p.medio)}: ${CLP(p.monto)}${ref}${cuotas}`, margin, y);
      y += 4.5;
    });
  } else if (venta.paymentMethod) {
    doc.text(`${labelMedio(venta.paymentMethod)}: ${CLP(venta.totalFinal || venta.total)}`, margin, y);
    y += 4.5;
  }
  if (venta.vuelto > 0) {
    doc.text(`Vuelto: ${CLP(venta.vuelto)}`, margin, y);
    y += 4.5;
  }

  renderStandardFooter(doc, branch);

  if (autoPrint) {
    doc.autoPrint();
    doc.output('dataurlnewwindow');
  }
  return doc;
}

export default generateBoletaPDF;
